import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import PilotBadge from "@/components/PilotBadge";
import Testimonials from "@/components/Testimonials";
import PartnerAppreciation from "@/components/PartnerAppreciation";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Rocket, Award, Briefcase, Landmark,
  GraduationCap, MapPin, ExternalLink,
} from "lucide-react";
import { IMG, ImageBreak } from "@/components/ImageAssets";

const partnerTypes = [
  "Veteran service organizations",
  "VA-adjacent and community providers",
  "Healthcare systems",
  "Mental health providers",
  "Workforce development agencies",
  "Colleges and universities",
  "Housing providers",
  "Food security organizations",
  "Legal aid organizations",
  "Transportation providers",
  "Local governments",
  "State agencies",
  "Philanthropic funders",
  "Corporate veteran programs",
  "Faith and community-based organizations",
  "LGBTQ+ affirming organizations",
  "Research & innovation partners",
];

const benefits = [
  "More accountable referrals",
  "Better coordination",
  "Reduced duplication",
  "Improved follow-up",
  "Stronger reporting",
  "Better service navigation",
  "Clearer community resource mapping",
  "Measurable outcomes for funders and stakeholders",
];

const entrepreneurResources = [
  {
    icon: <Landmark className="w-5 h-5" />,
    tag: "Federal",
    title: "SBA · Boots to Business",
    body: "Entrepreneurship training delivered through the U.S. Small Business Administration and Veterans Business Outreach Centers — business planning, financing, and federal contracts.",
    href: "https://www.sba.gov/business-guide/grow-your-business/veteran-owned-businesses",
  },
  {
    icon: <Rocket className="w-5 h-5" />,
    tag: "Nonprofit",
    title: "PenFed Foundation · VEP",
    body: "The Veteran Entrepreneur Program offers an Incubator track for idea-phase ventures and a Growth track for scaling established veteran-owned businesses.",
    href: "https://penfedfoundation.org/programs/veteran-entrepreneur-program/",
  },
  {
    icon: <Award className="w-5 h-5" />,
    tag: "Nonprofit",
    title: "Warrior Rising",
    body: "Mentorship, specialized training, and funding assistance to help veterans build the foundation of a successful business.",
    href: "https://www.warriorrising.org/",
  },
  {
    icon: <Briefcase className="w-5 h-5" />,
    tag: "VA · Federal",
    title: "VR&E Self-Employment Track",
    body: "VA Veteran Readiness and Employment self-employment track — business skill training, equipment support, and feasibility guidance for eligible veterans.",
    href: "https://www.va.gov/careers-employment/vocational-rehabilitation/",
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    tag: "Pennsylvania",
    title: "PA Business One-Stop Shop",
    body: "Pennsylvania-specific veteran business registration exemptions, regional SBDC assistance, and Veterans Business Outreach Center locations.",
    href: "https://www.business.pa.gov/",
  },
  {
    icon: <GraduationCap className="w-5 h-5" />,
    tag: "University",
    title: "Entrepreneurship Bootcamp for Veterans",
    body: "The EBV — a highly selective, first-of-its-kind experiential bootcamp for post-9/11 veterans, delivered through leading U.S. universities.",
    href: "https://ebv.vets.syr.edu/",
  },
];

export default function Partners() {
  return (
    <div data-testid="partners-page">
      <Seo
        title="Partners & Pilot Network | Warm Hand Off"
        description="Join the WHO Partner Network. Move from passive referrals to accountable, measurable warm handoffs coordinated by WHO Guides™. The Pittsburgh pilot is now open to veteran service organizations, healthcare and mental-health providers, workforce agencies, universities, nonprofits, and funders."
        path="/partners"
      />
      <PageHeader
        eyebrow="Partners"
        title="The partners who receive the warm handoff."
        lede="WHO Guides™ open the door. Your organization opens the next one. Move from passive referrals to accountable, measurable connection with the veterans, families, and community members you already serve every day."
      />

      {/* Provider-centered positioning — required statement */}
      <section className="py-10 bg-cream-panel border-b border-borderSubtle" data-testid="partners-positioning-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <p className="text-lg lg:text-xl text-navy font-semibold leading-relaxed max-w-4xl">
            &ldquo;WHO does not replace the organizations serving veterans. We are building an approach
            intended to help their work connect more effectively across the broader service ecosystem.&rdquo;
          </p>
          <p className="mt-4 text-sm text-slateSoft leading-relaxed max-w-3xl">
            We recognize the extraordinary commitment of the organizations and professionals who serve
            veterans — often across complex systems and under significant resource constraints. The challenge
            is fragmentation across systems, not a lack of commitment from the people working within them.
          </p>
        </div>
      </section>

      {/* Stronger Together — ecosystem infographic */}
      <section className="py-16 lg:py-20 bg-white border-b border-borderSubtle" data-testid="partners-ecosystem-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-10">
            <p className="eyebrow text-gold-deep">Stronger Together</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
              Partners across the transition ecosystem.
            </h2>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed">
              One ecosystem. Shared purpose. Better outcomes for every individual. WHO connects the
              organizations that already serve veterans and families — into a single accountable network.
            </p>
          </div>
          <div className="relative rounded-lg overflow-hidden shadow-xl border border-borderSubtle bg-cream" data-testid="partners-ecosystem-image-wrap">
            <img
              src="/assets/partners-ecosystem.png"
              alt="Stronger Together — Partners Across the Transition Ecosystem. A U.S. Air Force officer at the center surrounded by eight partner categories: Education, Community Organizations, Employers, Government Agencies, Housing, Workforce Partners, Health & Wellness, and Families & Mentors."
              className="w-full h-auto"
              loading="lazy"
              data-testid="partners-ecosystem-image"
            />
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-semibold uppercase tracking-widest text-slateSoft">
            <span className="flex items-center gap-2" data-testid="ecosystem-tag-collaborate">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" /> Collaborate
            </span>
            <span className="flex items-center gap-2" data-testid="ecosystem-tag-innovate">
              <span className="w-1.5 h-1.5 rounded-full bg-navy" /> Innovate
            </span>
            <span className="flex items-center gap-2" data-testid="ecosystem-tag-transform">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-deep" /> Transform Lives
            </span>
          </div>
        </div>
      </section>

      <PartnerAppreciation
        autoRotate
        title="A message from the partners who stand with our veterans."
      />

      <Section eyebrow="Who we invite" title="Partners across the transition ecosystem." id="partner-resources">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {partnerTypes.map((p) => (
            <div
              key={p}
              className="p-4 bg-white border border-borderSubtle rounded-md text-navy font-medium hover:border-gold transition-colors"
              data-testid={`partner-type-${p.toLowerCase().replace(/\W+/g, "-")}`}
            >
              {p}
            </div>
          ))}
        </div>
      </Section>

      {/* Better Coordination. Better Evidence. Stronger Communities. */}
      <section className="py-16 lg:py-20 bg-cream-panel border-b border-borderSubtle" data-testid="partners-evidence-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold-deep">The Economics of Connection</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-2xl lg:text-3xl">
              Better Coordination. Better Evidence. Stronger Communities.
            </h2>
            <p className="mt-5 text-slateSoft leading-relaxed">
              When connections are confirmed instead of assumed, everyone in the network learns something valuable: service organizations gain visibility into coordination activity and unresolved needs, community networks see where handoffs break down, institutional funders better understand unmet demand, and government agencies gain more useful evidence for planning and resource allocation.
            </p>
            <p className="mt-4 text-slateSoft leading-relaxed">
              WHO is seeking collaborative opportunities to test and validate this approach — beginning with the planned Pittsburgh pilot. Inclusion in the WHO Discovery directory does not constitute a partnership, endorsement, referral agreement, or participation in WHO&rsquo;s coordinated services by the listed organization.
            </p>
            <Link to="/impact" data-testid="partners-evidence-link" className="mt-6 inline-flex items-center gap-2 text-navy font-semibold underline decoration-gold underline-offset-4 hover:text-gold-deep transition-colors">
              Explore the impact vision <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Research & Innovation Partners — dedicated spotlight */}
      <section
        className="relative py-24 lg:py-28 bg-navy-deep border-y border-white/10 overflow-hidden"
        data-testid="research-innovation-section"
        id="research-innovation"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gold/15 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-[0.06]" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold" data-testid="research-eyebrow">
              Research &amp; Innovation Partners
            </p>
            <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl lg:text-5xl">
              Build the evidence base <br />with us.
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed">
              WHO Guides is not just infrastructure — it is a living dataset of how warm handoffs
              actually work at population scale. We invite research universities, policy institutes,
              federal research offices, and applied-AI labs to study, evaluate, and improve the
              Relationship Operating System with us.
            </p>
            <p className="mt-4 text-cream/60 leading-relaxed">
              Every research collaboration operates under consent-first data governance, IRB-aligned
              protocols, and a shared commitment to publish what works — and what doesn&apos;t.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="research-collab-cta"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Collaborate with us
              </Link>
              <a
                href="mailto:research@warmhandoffhq.com"
                data-testid="research-email-cta"
                className="inline-flex items-center gap-2 border border-cream/30 text-cream hover:bg-cream/10 font-semibold px-5 py-3 rounded-md transition-colors"
              >
                research@warmhandoffhq.com
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { t: "Research Universities", b: "Schools of public policy, social work, health services research, human-centered design, and computer science.", tag: "R1 / R2 / HBCUs / MSIs" },
                { t: "Policy Institutes", b: "Nonpartisan think tanks and policy centers focused on veterans, workforce, housing, health equity, and social safety-net modernization.", tag: "RAND · Urban · IVMF" },
                { t: "Federal Research Offices", b: "VA HSR&D, DoD PSHR, HHS ACL, HRSA — the research arms of the agencies whose missions WHO exists to strengthen.", tag: "VA · DoD · HHS · HRSA" },
                { t: "Applied AI & Data Labs", b: "University AI labs, civic-tech centers, and applied-ML groups studying human-in-the-loop coordination and voice-native interaction at scale.", tag: "HITL · NLP · Voice" },
                { t: "Outcomes Research Partners", b: "Evaluation firms and academic teams designing rigorous studies of warm handoffs, retention, referral completion, and downstream well-being.", tag: "RCTs · Quasi-experimental" },
                { t: "Data & Ethics Partners", b: "Data governance, privacy, and AI-ethics organizations helping us set the standard for consent-first coordination.", tag: "Consent · Privacy · IRB" },
              ].map((r) => (
                <div
                  key={r.t}
                  className="p-5 bg-white/[0.04] border border-white/10 hover:border-gold/50 rounded-lg transition-colors"
                  data-testid={`research-card-${r.t.toLowerCase().replace(/\W+/g, "-")}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-bold">{r.tag}</p>
                  </div>
                  <h3 className="mt-2 font-display font-bold text-cream text-lg leading-tight">{r.t}</h3>
                  <p className="mt-2 text-cream/70 text-sm leading-relaxed">{r.b}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-5 bg-gold/10 border border-gold/40 rounded-md">
              <p className="text-cream text-sm leading-relaxed">
                <span className="font-bold text-gold">Founding research partners.</span> WHO is
                seeking a small cohort of founding academic and policy partners for a longitudinal
                study of the Pittsburgh pilot. Co-authorship, shared IRB, and joint publication rights.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Section eyebrow="Why join the WHO Network" title="Move from passive referrals to accountable connection." bg="cream">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((b) => (
                <div key={b} className="flex gap-4 p-5 bg-white border border-borderSubtle rounded-md" data-testid={`benefit-${b.toLowerCase().replace(/\W+/g, "-")}`}>
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <span className="text-navy leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-lg overflow-hidden border border-borderSubtle shadow-md">
              <img
                src={IMG.communityMeeting}
                alt="Diverse community leaders and partners in conversation"
                className="w-full h-[420px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-cream">
                <p className="eyebrow text-gold-light">Partner network</p>
                <p className="mt-1 font-display font-bold text-lg leading-tight">Coordinate. Confirm. Connect.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="Pittsburgh · Pilot Launch" title="Building the first coordinated warm-handoff network in Western PA.">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-lg text-slateSoft leading-relaxed">
              WHO is inviting Pittsburgh-area and Pennsylvania partners — veteran service
              organizations, healthcare systems, workforce boards, colleges, nonprofits, and
              philanthropic funders — to help shape a live pilot. Early partners will co-design
              intake, referral, and outcome practices that scale across communities.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Early access to the WHO platform",
                "Co-design workshops with partner agencies",
                "Shared outcome dashboards across the network",
                "Community resource mapping across Western PA",
              ].map((it) => (
                <li key={it} className="flex gap-3 text-navy" data-testid={`pilot-benefit-${it.toLowerCase().replace(/\W+/g, "-")}`}>
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-gold" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 p-8 bg-navy text-cream rounded-lg">
            <p className="eyebrow text-gold-light">Three ways to engage</p>
            <div className="mt-6 space-y-4">
              <PartnerCta to="/contact" label="Become a Pilot Partner" testId="partner-cta-pilot" />
              <PartnerCta to="/contact" label="Join the WHO Network" testId="partner-cta-join" />
              <PartnerCta to="/contact" label="Schedule a Conversation" testId="partner-cta-schedule" />
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <a
                href="/assets/who-pilot-overview.pdf"
                download
                data-testid="partner-cta-download-pilot-pdf"
                className="flex items-center justify-between w-full px-5 py-4 bg-gold hover:bg-gold-deep text-navy font-semibold rounded-md transition-colors group"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download Pilot Overview (PDF)
                </span>
                <span className="text-[10px] uppercase tracking-widest bg-navy/90 text-cream px-2 py-1 rounded">2pg</span>
              </a>
            </div>
          </div>
        </div>
      </Section>

      <Section eyebrow="Featured Resources · Veteran Entrepreneurship" title="Specialized training, mentorship, and funding for veterans building businesses." bg="cream">
        <p className="text-lg text-slateSoft leading-relaxed max-w-3xl mb-10">
          Veteran entrepreneur programs offer tailored training, mentorship, and funding guidance
          for former service members. Below are trusted programs WHO can connect veterans to — from
          idea-stage incubators to intensive university bootcamps and federal self-employment tracks.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entrepreneurResources.map((r, i) => (
            <a
              key={r.title}
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`entrepreneur-resource-${i}`}
              className="group p-7 bg-white border border-borderSubtle rounded-lg hover:border-gold hover:shadow-md transition-all block"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-md bg-navy text-gold">
                  {r.icon}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold-deep bg-gold/10 px-2.5 py-1 rounded">
                  {r.tag}
                </span>
              </div>
              <h3 className="mt-5 font-display font-bold text-navy text-lg leading-tight">{r.title}</h3>
              <p className="mt-3 text-slateSoft leading-relaxed">{r.body}</p>
              <div className="mt-5 pt-4 border-t border-borderSubtle flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-gold-deep transition-colors">
                Visit program <ExternalLink className="w-3.5 h-3.5" />
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 p-6 bg-white border border-borderSubtle rounded-md">
          <p className="text-sm text-slateSoft leading-relaxed">
            <span className="font-semibold text-navy">Note:</span> WHO does not administer these
            programs. As warm-handoff infrastructure, WHO helps partner organizations connect
            veterans to the right program, confirm the connection is made, and track outcomes
            across the ecosystem.
          </p>
        </div>
      </Section>

      <Testimonials eyebrow="Partner Voices" title="What early pilot partners are seeing." />

      <CtaBanner
        eyebrow="Together."
        title="Coordinate around the person. Connect the community."
        primaryText="Join the Partner Network"
        primaryTo="/contact"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}

function PartnerCta({ to, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center justify-between w-full px-5 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold rounded-md transition-colors group"
    >
      <span className="font-semibold">{label}</span>
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}
